import { useState, useEffect, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { InventoryRequestsService, LocationsService } from '../../../api';
import {
  mapApiRequestToDetail,
  mapApiRequestToRow,
  mapUiStatusToApiStatus,
  type InventoryRequestRow,
  type RequestDetail,
  type RequestDetailItem,
  type RequestFilterState,
  type RequestPaginationState,
} from '../../../utils/inventory/inventoryRequestMappers';
import type { NotificationSeverity } from '../../../utils/constants/notificationMessages';

export type {
  InventoryRequestApiStatus,
  InventoryRequestRow,
  RequestDetail,
  RequestDetailItem,
  RequestFilterState,
  RequestPaginationState,
} from '../../../utils/inventory/inventoryRequestMappers';

interface LocationOption {
  id: number;
  name: string;
}

const calculateTotalPages = (total: number, itemsPerPage: number) => {
  return Math.ceil(total / itemsPerPage);
};

const EMPTY_FILTERS: RequestFilterState = {
  requestCode: '',
  sourceLocation: '',
  destinationLocation: '',
  dateFrom: '',
  dateTo: '',
  status: '',
};

export const useInventoryRequests = () => {
  const [requests, setRequests] = useState<InventoryRequestRow[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [filters, setFilters] = useState<RequestFilterState>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<RequestFilterState>(EMPTY_FILTERS);
  const [pagination, setPagination] = useState<RequestPaginationState>({
    total: 0,
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequestDetail, setSelectedRequestDetail] = useState<RequestDetail | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: NotificationSeverity;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await LocationsService.locationsControllerFindAll();
        const list = response?.data ?? response;
        if (Array.isArray(list)) {
          setLocations(
            list.map((location: { id: number; name: string }) => ({
              id: Number(location.id),
              name: location.name,
            })),
          );
        }
      } catch (error) {
        console.error('Error al cargar ubicaciones:', error);
      }
    };

    fetchLocations();
  }, []);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await InventoryRequestsService.inventoryRequestsControllerFindAll(
        pagination.currentPage,
        pagination.itemsPerPage,
        appliedFilters.requestCode !== '' ? appliedFilters.requestCode : undefined,
        mapUiStatusToApiStatus(appliedFilters.status),
        appliedFilters.sourceLocation !== '' ? Number(appliedFilters.sourceLocation) : undefined,
        appliedFilters.destinationLocation !== ''
          ? Number(appliedFilters.destinationLocation)
          : undefined,
        appliedFilters.dateFrom !== '' ? appliedFilters.dateFrom : undefined,
        appliedFilters.dateTo !== '' ? appliedFilters.dateTo : undefined,
      );

      const responseData = response?.data ?? response;
      const rows = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];
      const meta = responseData?.meta;

      const formatted = rows.map((row: Record<string, unknown>) => mapApiRequestToRow(row));
      setRequests(formatted);
      setPagination((prev) => ({
        ...prev,
        total: Number(meta?.total ?? formatted.length),
        currentPage: Number(meta?.page ?? prev.currentPage),
        totalPages: Number(
          meta?.totalPages ??
            calculateTotalPages(Number(meta?.total ?? formatted.length), prev.itemsPerPage),
        ),
      }));
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      setRequests([]);
      setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
      setNotification({
        open: true,
        message: 'No se pudieron cargar las solicitudes de inventario.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.destinationLocation,
    appliedFilters.requestCode,
    appliedFilters.sourceLocation,
    appliedFilters.status,
    pagination.currentPage,
    pagination.itemsPerPage,
  ]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests, searchTrigger]);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const name = e.target.name as string;
      const value = e.target.value as string;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleRequestCodeKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setAppliedFilters(filters);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        setSearchTrigger((prev) => prev + 1);
      }
    },
    [filters],
  );

  const handleResetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleSearch = useCallback(() => {
    setIsSearching(true);
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
    setIsSearching(false);
  }, [filters]);

  const clearAppliedFilterField = useCallback((field: keyof RequestFilterState) => {
    setAppliedFilters((prev) => {
      const next = { ...prev, [field]: '' };
      setFilters(next);
      return next;
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  /** Deep-link from related entities: filter list by Código SOL and keep it after modal close. */
  const applyDeepLinkFilter = useCallback((requestCode: string) => {
    const nextFilters: RequestFilterState = { ...EMPTY_FILTERS, requestCode };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleItemsPerPageChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const newItemsPerPage = Number(event.target.value);
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: newItemsPerPage,
      currentPage: 1,
      totalPages: calculateTotalPages(prev.total, newItemsPerPage),
    }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const toggleMoreFilters = useCallback(() => {
    setShowMoreFilters((prev) => !prev);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedRequestDetail(null);
  }, []);

  const handleViewDetails = useCallback(async (requestId: string) => {
    setIsLoadingDetails(true);
    setShowDetailsModal(true);

    try {
      const response = await InventoryRequestsService.inventoryRequestsControllerFindOne(
        Number(requestId),
      );
      const request = (response?.data ?? response) as Record<string, unknown>;
      setSelectedRequestDetail(mapApiRequestToDetail(request));
    } catch (error) {
      console.error('Error al cargar detalle de solicitud:', error);
      setSelectedRequestDetail(null);
      setNotification({
        open: true,
        message: 'No se pudo cargar el detalle de la solicitud.',
        severity: 'error',
      });
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    requests,
    locations,
    filters,
    appliedFilters,
    pagination,
    isLoading,
    isSearching,
    showMoreFilters,
    showDetailsModal,
    selectedRequestDetail,
    isLoadingDetails,
    notification,
    handleFilterChange,
    handleSelectChange,
    handleRequestCodeKeyPress,
    handleResetFilters,
    handleSearch,
    clearAppliedFilterField,
    applyDeepLinkFilter,
    handlePageChange,
    handleItemsPerPageChange,
    toggleMoreFilters,
    handleCloseModal,
    handleViewDetails,
    handleCloseNotification,
    fetchRequests,
    setNotification,
  };
};

export type ItemResolutionState = Record<
  number,
  {
    approved: boolean;
    rejectionReason: string;
  }
>;

export function buildInitialResolutionState(items: RequestDetailItem[]): ItemResolutionState {
  return items.reduce<ItemResolutionState>((acc, item) => {
    acc[item.id] = { approved: true, rejectionReason: '' };
    return acc;
  }, {});
}

export function buildResolvePayload(resolution: ItemResolutionState) {
  return {
    items: Object.entries(resolution).map(([id, value]) => ({
      id: Number(id),
      approved: value.approved,
      rejectionReason:
        !value.approved && value.rejectionReason.trim() ? value.rejectionReason.trim() : undefined,
    })),
  };
}
